import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BlogPostMetadata } from '@/types/blog';
import { getAllPosts, formatBlogDate, getDatePathFromIsoString } from '@/utils/blog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface RelatedBlogPostsProps {
  currentPostTags: string[];
  currentPostSlug: string;
  maxPosts?: number;
}

const RelatedBlogPosts: React.FC<RelatedBlogPostsProps> = ({ 
  currentPostTags,
  currentPostSlug,
  maxPosts = 3 
}) => {
  const [relatedPosts, setRelatedPosts] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelated = async () => {
      setLoading(true);
      try {
        const allPosts = await getAllPosts();
        const filtered = allPosts
          .filter(post => {
            // Exclude the current post
            if (post.slug === currentPostSlug) return false;
            // Check for shared tags (case-insensitive)
            return post.tags.some(tag => 
              currentPostTags.map(t => t.toLowerCase()).includes(tag.toLowerCase())
            );
          })
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by newest first
          .slice(0, maxPosts);
        setRelatedPosts(filtered);
      } catch (error) {
        console.error("Error fetching related blog posts:", error);
        setRelatedPosts([]); // Set to empty on error
      }
      setLoading(false);
    };

    if (currentPostTags.length > 0) {
      fetchRelated();
    }
  }, [currentPostTags, currentPostSlug, maxPosts]);

  if (loading) {
    return (
      <div className="mt-12 py-8 border-t border-gray-800">
        <h3 className="text-xl font-semibold text-white mb-6">Related Articles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(maxPosts)].map((_, i) => (
            <Card key={i} className="bg-gray-800 border-gray-700 overflow-hidden">
              <Skeleton className="h-40 w-full" />
              <CardContent className="p-4">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2 mt-1" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (relatedPosts.length === 0) {
    return null; // Don't render anything if no related posts found (or if currentPostTags is empty initially)
  }

  return (
    <div className="mt-12 py-8 border-t border-gray-800">
      <h3 className="text-xl font-semibold text-white mb-6">Related Articles</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {relatedPosts.map(post => {
          const datePath = getDatePathFromIsoString(post.date);
          const postUrl = `/blog/${datePath}/${post.slug}`;
          return (
            <Card key={post.slug} className="bg-gray-800 border-gray-700 hover:border-indigo-600 transition-all duration-200 flex flex-col overflow-hidden group">
              {post.thumbnail && (
                <Link to={postUrl} reloadDocument className="block overflow-hidden h-40 w-full">
                  <img 
                    src={post.thumbnail} 
                    alt={post.title} 
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </Link>
              )}
              <CardContent className="p-4 flex flex-col flex-grow">
                <Link to={postUrl} reloadDocument>
                  <h4 className="text-md font-semibold text-gray-100 group-hover:text-indigo-400 mb-2 line-clamp-2">{post.title}</h4>
                </Link>
                <p className="text-xs text-gray-400 mb-3 line-clamp-3 flex-grow">{post.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                  <span>{formatBlogDate(post.date)}</span>
                  <span>{post.readingTime?.text}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedBlogPosts;
